import { useMemo } from 'react';

import Tippy from '@tippyjs/react';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { useUIContext } from 'lib/context/UIContext/UIContext';
import { camelCaseToReadableFormat, ia, pluralize, singularize } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import Export from 'components/shared/Export/Export';
import Header from 'components/shared/Header/Header';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';

import { SPECIAL_UPPERCASE_NAMES } from '../lib/constants';
import { ALLOWED_PRINT_EXPORT_CATEGORIES } from '../lib/helpers';

import ViewTabs from './ViewTabs';

const TableHeader = ({
  category,
  onModalToggle,
  hasNew,
  subtitle,
  actions,
  refresh,
  onRefresh,
  noTitle,
  switchItems,
  rightActions,
  loading,
  newButtonText,
  statusPanelData = []
}) => {
  const tableContext = useTableContext();

  const {
    filters,
    title,
    gridApi,
    exportParams = {},
    updateFilters,
    resetFilters,
    changeFilters,
    ...rest
  } = tableContext;

  const { device } = useUIContext();

  const isMobile = device === 'mobile' || device === 'tablet-sm';
  const isTablet = device === 'tablet' || device === 'tablet-lg';
  const isSmallerDevice = isMobile || isTablet;

  const totalSelected = useMemo(() => {
    return rest.selectedRows?.length;
  }, [rest.selectedRows]);

  const categoryName = SPECIAL_UPPERCASE_NAMES.includes(category)
    ? category.toUpperCase()
    : camelCaseToReadableFormat(category);

  const printExportConfig = useMemo(() => ALLOWED_PRINT_EXPORT_CATEGORIES[category], [category]);

  const smallDeviceOptions = [
    isMobile &&
      hasNew && {
        label: `New ${singularize(categoryName)}`,
        onClick: onModalToggle,
        icon: 'add-square',
        color: 'primary'
      }
  ].filter(Boolean);

  const options = smallDeviceOptions.filter(Boolean);

  return (
    <Header
      title={!noTitle && (title || categoryName)}
      subtitle={subtitle}
      childrenClassName="flex items-center gap-3 justify-between flex-1">
      <ViewTabs
        items={switchItems}
        category={category}
        filters={filters}
        title={title}
        updateFilters={updateFilters}
        resetFilters={resetFilters}
      />
      <div className="flex flex-1 items-center justify-end gap-3">
        {typeof actions === 'function'
          ? actions({ category, filters, loading, setFilters: changeFilters, ...rest, gridApi })
          : actions}
        {totalSelected > 0 && actions && (
          <Typography
            variant="label"
            weight="medium"
            color="neutral-700"
            size="small"
            className="whitespace-nowrap">
            {totalSelected} {pluralize('item', totalSelected)} selected
          </Typography>
        )}
        {!isSmallerDevice && refresh && (
          <Tippy content="Refresh" placement="bottom" theme="dark">
            <Button
              icon="refresh-linear"
              style="outlined"
              color="neutral"
              iconIsStroke
              onClick={onRefresh}
            />
          </Tippy>
        )}

        <Export
          showLabel
          useData={() => ({
            ...tableContext,
            ...exportParams,
            statusPanelData: printExportConfig?.withTotals && statusPanelData
          })}
        />

        {!isMobile && hasNew && (
          <Button
            text={`New ${singularize(newButtonText || categoryName)}`}
            icon="add-square-mono"
            size="xs"
            variant="link"
            onClick={onModalToggle}
            textClassName="whitespace-nowrap"
          />
        )}
      </div>
      {typeof rightActions === 'function'
        ? rightActions({ category, loading, filters, setFilters: changeFilters, ...rest })
        : rightActions}
      {ia(options) && <Popover options={options} position="left" />}
    </Header>
  );
};

export default TableHeader;
